/**
 * Admin Moderation - Inquiries Tab
 * View and respond to contact form submissions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  MessageSquare, Mail, User, Calendar, CheckCircle, Check,
  Loader2, RefreshCw, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const AdminInquiries = ({ onCountChange }) => {
  const { apiCall, formatDate } = useAdmin();
  
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, responded, archived
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  
  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const data = await apiCall('get', `/api/admin/inquiries${params}`);
      const inquiriesList = data.inquiries || data || [];
      setInquiries(inquiriesList);
      // Update parent count - count only 'new' status inquiries
      if (onCountChange) {
        const newCount = filter === 'new' 
          ? inquiriesList.length 
          : inquiriesList.filter(i => i.status === 'new').length;
        onCountChange(newCount);
      }
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, filter, onCountChange]);
  
  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);
  
  const handleDone = async (inquiryId) => {
    try {
      // API still uses 'archive' endpoint, but UI shows 'Done'
      await apiCall('put', `/api/admin/inquiries/${inquiryId}/archive`);
      toast.success('Inquiry marked as done');
      // Update count immediately - only affects 'new' status inquiries
      if (onCountChange) {
        onCountChange(prev => Math.max(0, prev - 1));
      }
      fetchInquiries();
    } catch (error) {
      console.error('Failed to mark inquiry as done:', error);
    }
  };
  
  const handleMarkRead = async (inquiryId) => {
    try {
      await apiCall('put', `/api/admin/inquiries/${inquiryId}/read`);
      // Note: marking as read does NOT change status from 'new', so don't update count
      fetchInquiries();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };
  
  const getTypeBadge = (type) => {
    const colors = {
      general: 'bg-blue-500/20 text-blue-600',
      support: 'bg-green-500/20 text-green-600',
      business: 'bg-purple-500/20 text-purple-600',
      feedback: 'bg-amber-500/20 text-amber-600',
      bug: 'bg-red-500/20 text-red-600',
      advertise: 'bg-pink-500/20 text-pink-600',
      suggestion: 'bg-teal-500/20 text-teal-600'
    };
    return colors[type] || colors.general;
  };
  
  const formatPlacement = (placement) => {
    const labels = {
      'homepage_top': 'Homepage Top Banner',
      'homepage_middle': 'Homepage Middle (2-Up)',
      'homepage_bottom': 'Homepage Bottom Banner',
      'category_landing': 'Category Landing Pages',
      'in_grid_ads': 'In-Grid Ads (Marketplace)',
      'multiple': 'Multiple Locations',
      'not_sure': 'Not Sure Yet'
    };
    return labels[placement] || placement || 'Not specified';
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-primary" />
            Contact Inquiries
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage contact form submissions
          </p>
        </div>
        <button
          onClick={fetchInquiries}
          disabled={loading}
          className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'all', label: 'All' },
          { id: 'new', label: 'New' },
          { id: 'responded', label: 'Responded' },
          { id: 'done', label: 'Done', apiStatus: 'archived' }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.apiStatus || f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === (f.apiStatus || f.id)
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      
      {/* Inquiries List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No inquiries found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {inquiries.map(inquiry => (
              <div 
                key={inquiry.id}
                className={`p-4 hover:bg-secondary/20 transition-colors cursor-pointer ${
                  !inquiry.read ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                }`}
                onClick={() => {
                  setSelectedInquiry(inquiry);
                  if (!inquiry.read) handleMarkRead(inquiry.id);
                }}
              >
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-medium">
                          {inquiry.name || 'Anonymous'}
                          {!inquiry.read && (
                            <span className="ml-2 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                              New
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {inquiry.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeBadge(inquiry.type)}`}>
                          {inquiry.type || 'General'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(inquiry.created_at)}
                        </span>
                      </div>
                    </div>
                    
                    <p className="mt-2 text-sm font-medium">{inquiry.subject}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {inquiry.message}
                    </p>
                    
                    {/* Show preferred placement for advertise inquiries */}
                    {inquiry.type === 'advertise' && inquiry.preferred_placement && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-pink-600">
                        <MapPin className="w-3 h-3" />
                        {formatPlacement(inquiry.preferred_placement)}
                      </div>
                    )}
                    
                    {inquiry.responded && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                        <Check className="w-3 h-3" />
                        Responded
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Inquiry Detail Dialog */}
      <Dialog open={!!selectedInquiry} onOpenChange={() => setSelectedInquiry(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Inquiry Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-4">
              {/* Sender Info */}
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{selectedInquiry.name}</div>
                  <div className="text-sm text-muted-foreground">{selectedInquiry.email}</div>
                  {selectedInquiry.company && (
                    <div className="text-sm text-muted-foreground">{selectedInquiry.company}</div>
                  )}
                  {selectedInquiry.phone && (
                    <div className="text-sm text-muted-foreground">{selectedInquiry.phone}</div>
                  )}
                </div>
              </div>
              
              {/* Message */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeBadge(selectedInquiry.type)}`}>
                    {selectedInquiry.type || 'General'}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(selectedInquiry.created_at)}
                  </span>
                </div>
                
                {/* Preferred Placement for advertise inquiries */}
                {selectedInquiry.type === 'advertise' && selectedInquiry.preferred_placement && (
                  <div className="flex items-center gap-2 p-2 bg-pink-500/10 rounded-lg">
                    <MapPin className="w-4 h-4 text-pink-500" />
                    <span className="text-sm font-medium text-pink-600">
                      Preferred Placement: {formatPlacement(selectedInquiry.preferred_placement)}
                    </span>
                  </div>
                )}
                
                <h3 className="font-semibold">{selectedInquiry.subject}</h3>
                <p className="text-sm whitespace-pre-wrap">{selectedInquiry.message}</p>
              </div>
              
              {/* Previous Replies */}
              {selectedInquiry.replies && selectedInquiry.replies.length > 0 && (
                <div className="space-y-2 border-t border-border pt-4">
                  <h4 className="text-sm font-medium">Previous Replies</h4>
                  {selectedInquiry.replies.map((reply, idx) => (
                    <div key={idx} className="p-3 bg-green-500/10 rounded-lg border-l-2 border-green-500">
                      <p className="text-sm">{reply.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {reply.admin_name} • {formatDate(reply.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Contact Actions */}
              <div className="space-y-3 border-t border-border pt-4">
                <label className="text-sm font-medium">Contact via:</label>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`mailto:${selectedInquiry.email}`}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </a>
                  {selectedInquiry.phone && (
                    <a
                      href={`tel:${selectedInquiry.phone}`}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <User className="w-4 h-4" />
                      Call
                    </a>
                  )}
                  <button
                    onClick={() => handleDone(selectedInquiry.id)}
                    className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInquiries;
